<template>
  <div>
    <div class="banner relative pt-4 pb-6" :class="{ 'md:rounded-t': roundedCorners }">
      <img
        :src="require('@/assets/images/backgrounds/handshake-trade.jpg')"
        :srcset="
          require('@/assets/images/backgrounds/handshake-trade-mobile.jpg') +
          ' 640w, ' +
          require('@/assets/images/backgrounds/handshake-trade.jpg') +
          ' 2048w'
        "
        alt="Farming Handshake Trade"
        class="image-cover object-bottom absolute bottom-0 top-0 left-0 right-0 z-1"
      />

      <div class="container-padding text-center text-white relative z-3">
        <h1 class="font-medium text-5xl sm:text-6xl">Secure Payments</h1>
        <h2 class="font-medium text-xl sm:text-3xl mt-2">The Safest Way To Buy & Sell</h2>

        <p class="sm:text-xl mt-4">
          Trade with confidence, knowing you&rsquo;re protected by SellMyLivestock
        </p>

        <UtilButton @click="openSecurePaymentModal()" color="primary" class="mt-8">
          {{ $t('buttons.createSecurePayment') }}
        </UtilButton>

        <UtilTextLink
          v-if="$store.state.securePayments.existingApi.data.length > 0"
          to="/account/payments"
          class="block mt-4"
        >
          View existing Secure Payments
        </UtilTextLink>
      </div>
    </div>

    <div class="flex flex-wrap lg:flex-no-wrap justify-center sm:items-stretch -mt-4 lg:-mt-8 relative z-2">
      <div
        v-for="step in compSteps"
        :key="step.title"
        class="step w-full sm:w-4/12 xl:w-3/12 mb-4 px-2 lg:px-0 lg:mb-0 lg:mx-2 flex items-stretch"
      >
        <div class="relative bg-white w-full rounded shadow-xl hover:shadow-2xl">
          <UtilTextLink
            :to="step.path"
            class="z-3 relative px-5 pt-5 pb-4 sm:pb-16 lg:pb-4 rounded no-underline flex flex-col h-full"
          >
            <span class="block font-medium text-xl lg:text-3xl">
              <FontAwesomeIcon :icon="step.icon" class="mr-2" />
              {{ step.title }}
            </span>

            <span class="mt-4 block flex-auto">
              {{ step.body }}
            </span>
          </UtilTextLink>
        </div>
      </div>
    </div>

    <div class="bg-gray-lightest container-padding-x py-8">
      <div class="max-w-3xl mx-auto">
        <div class="text-center">
          <FontAwesomeIcon :icon="faHandshake" class="text-6xl text-brand" />
          <h3 class="font-medium text-3xl mt-2">Why Secure Payments</h3>
        </div>

        <ul class="md:column-count-2 mt-4 sm:mt-8">
          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            We verify the identity of all parties.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            We help with all disputes.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            Buyers secure their purchases.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            Customise transactions.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            We protect both sides of the transaction.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            For dealing with someone you don’t know.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            Funds are held securely in our vault.
          </li>

          <li class="mb-2">
            <FontAwesomeIcon :icon="faCheck" class="text-brand mr-2" />
            Just 1% commission.
          </li>
        </ul>
      </div>
    </div>

    <div class="bg-white container-padding-x py-8">
      <UtilSPSteps class="max-w-6xl mx-auto" />
    </div>

    <div v-if="appIs.auth" class="bg-gray-lightest">
      <div class="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
        <p class="text-center text-3xl font-medium">
          Supported Online Banking Providers
        </p>

        <p class="mt-4 text-gray-darker text-center max-w-3xl mx-auto">
          If your banking provider is not listed, you can still use our secure payments facility. This can be done
          using manual BACS transfers into our Secure Vault, keeping you protected when trading online.
        </p>

        <div class="mt-6 lg:mt-8 sm:column-count-2 md:column-count-3">
          <div v-for="provider in supportedBanks" :key="provider.id" class="flex items-center mt-2 bank">
            <div class="flex-shrink-0 mr-4 p-2">
              <div class="h-12 w-12 relative">
                <UtilImage :src="provider.icon" :alt="provider.displayableName" background rounded />
              </div>
            </div>

            <div class="text-xl">
              {{ provider.displayableName }}
            </div>
          </div>
        </div>

        <p v-if="$breakpoint.xs" class="mt-4 text-gray-darker text-center max-w-lg mx-auto">
          If your banking provider is not listed, you can still use our secure payments facility. This can be done
          using manual BACS transfers into our Secure Vault, keeping you protected when trading online.
        </p>
      </div>
    </div>

    <div class="footer-banner relative pt-4 pb-6" :class="{ 'md:rounded-b': roundedCorners }">
      <div class="container-padding text-center text-white relative z-3">
        <h2 class="font-medium text-xl sm:text-3xl mt-2">Buying & Selling Has Never Been Easier</h2>

        <p class="sm:text-xl mt-4">
          Start Trading Today
        </p>

        <UtilButton @click="openSecurePaymentModal()" color="primary" class="mt-8">
          {{ $t('buttons.createSecurePayment') }}
        </UtilButton>
      </div>
    </div>
  </div>
</template>

<script>
  import {faCheck, faHandshake, faTruck, faUniversity} from '@fortawesome/pro-regular-svg-icons'
  import {mapGetters} from 'vuex'
  import UtilSPSteps from '@/components/utils/payment/secure-payments/UtilSPSteps'
  import UtilButton from '@/components/utils/UtilButton'
  import UtilTextLink from '@/components/utils/UtilTextLink'
  import UtilImage from '@/components/utils/UtilImage'

  export default {
  name: 'UtilSPLanding',
  components: {
    UtilSPSteps,
    UtilButton,
    UtilTextLink,
    UtilImage
  },
  props: {
    roundedCorners: Boolean
  },
  data() {
    return {
      faHandshake,
      faCheck
    }
  },
  created() {
    if (this.appIs.auth) {
      this.$store.dispatch('securePayments/getExisting')
    }

    this.$store.dispatch('securePayments/getTrueLayerSupportedBanks')
  },
  computed: {
    ...mapGetters({
      supportedBanks: 'securePayments/supportedBanks'
    }),

    compSteps() {
      return [
        {
          title: '1. Agree a Deal',
          body:
            'Both parties agree to a deal and the Buyer transfers funds into our Secure Vault. Where it will be held securely.',
          icon: faHandshake
        },
        {
          title: '2. Confirm Movement',
          body:
            'The Seller confirms the stock has been collected or hauled, with full confidence knowing their payment is secured.',
          icon: faTruck
        },
        {
          title: '3. Release Funds',
          body:
            'The Buyer releases the funds to the Seller once they have received their purchase. If the Buyer takes no action, the funds will be relased after 48 hours.',
          icon: faUniversity
        }
      ]
    }
  },
  methods: {
    openSecurePaymentModal() {
      this.$modal.open('ModalSecurePayment')
    }
  }
}
</script>

<style lang="scss" scoped>
.banner {
  background-color: #585146;
}

.footer-banner {
  background: #5e5353 url('~@/assets/images/backgrounds/tablet-livestock.jpg') no-repeat center center;
  background-size: 100% auto;
}

#app.smAndDown {
  .footer-banner {
    background-size: auto 100%;
  }
}

.bank {
  break-inside: avoid;
}
</style>
